import _ from "lodash";
import React, { useEffect, useLayoutEffect, memo, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { Button, Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";

import { useI18n } from "Hooks";
import { formatDate, checkIsDateSame } from "Utils/DateTimeHelpers";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";
import { getIsAIDebugModeEnabled, scrollToBottom } from "Utils/MessageHelpers";

import classes from "./SummaryMessage.scss";

const styles = {
  aiStripContainerStyle: {
    borderRadius: "0px 0px 8px 8px",
  },
  overlayTooltipStyles: {
    maxWidth: "280px",
  },
};

const FORMATS = {
  START: "D MMM",
  END: "D MMM YYYY",
};

const SummaryMessage = props => {
  const {
    isGeneratingSummary,
    message,
    cancelSummaryGeneration,
    getDebugPropsForSummaryMessage,
    removeDebugPropsForSummaryMessage,
  } = props;
  const { id: messageId, label, summaryRange } = message;
  const { start, end } = summaryRange || {};

  const { t } = useI18n();
  const { chatId, conversationId } = useMessageOptions();
  const parentId = chatId || conversationId;

  // TODO: remove the following logic once we start saving summary messages on backend
  useEffect(
    () => () => {
      // Note: As of now we are not saving summary messages on backend
      // so they get removed from redux state when component unmounts
      // thus removing debug props for summary message.
      removeDebugPropsForSummaryMessage?.(messageId);
    },
    [messageId, removeDebugPropsForSummaryMessage]
  );

  useLayoutEffect(() => {
    if (!isGeneratingSummary) return;

    // we are generating summary and label is changed, so scroll to bottom
    scrollToBottom({ parentId, scrollInNextTick: true });
  }, [isGeneratingSummary, label, parentId]);

  const onClickStop = () => cancelSummaryGeneration?.(chatId || conversationId);

  const aiDebugModeObj = getDebugPropsForSummaryMessage?.(messageId);

  const title = isGeneratingSummary
    ? t("common:summarising")
    : t("common:summary");

  const formattedDateSuffix = useMemo(() => {
    if (!start || !end) return "";

    const formattedDate = checkIsDateSame({ date1: start, date2: end })
      ? formatDate({ date: end, format: FORMATS.END })
      : `${formatDate({
          date: start,
          format: FORMATS.START,
        })} - ${formatDate({
          date: end,
          format: FORMATS.END,
        })}`;
    return `(${formattedDate})`;
  }, [start, end]);

  return (
    <div className={classes.container}>
      <div className={classes.innerContainer}>
        <div className={classes.upperRow}>
          <div className={classes.titleAndLoader}>
            <div
              className={classNames(
                "text-textSubtle",
                "text-label-3",
                classes.title
              )}
            >
              {`${title} ${formattedDateSuffix}`}
            </div>
            {isGeneratingSummary ? (
              <span className={classes.animatedDotLoading} />
            ) : (
              <Tooltip
                tooltip={t("common:summary_only_visible_to_you")}
                overlayInnerStyle={styles.overlayTooltipStyles}
              >
                <InformationOutlined size="xxx-small" variant="subtle" />
              </Tooltip>
            )}
          </div>
          {isGeneratingSummary ? (
            <Button size="small" variant="inline" onClick={onClickStop}>
              {t("common:stop")}
            </Button>
          ) : null}
        </div>

        <div
          className={classNames(
            "text-textDefault",
            "text-body-2",
            classes.summary
          )}
        >
          {label}
        </div>
      </div>

      {!isGeneratingSummary &&
      getIsAIDebugModeEnabled() &&
      !_.isEmpty(aiDebugModeObj) ? (
        <AIFeedbackStrip
          {...aiDebugModeObj}
          containerStyle={styles.aiStripContainerStyle}
        />
      ) : null}
    </div>
  );
};

SummaryMessage.propTypes = {
  message: PropTypes.object.isRequired,
  isGeneratingSummary: PropTypes.bool.isRequired,
  cancelSummaryGeneration: PropTypes.func.isRequired,
  getDebugPropsForSummaryMessage: PropTypes.func.isRequired,
  removeDebugPropsForSummaryMessage: PropTypes.func.isRequired,
};

export default memo(SummaryMessage);
